/**
 * Policy resolution for the browser host.
 *
 * The policy itself, its defaults and its validation all live in `@evolveos/focus-core`.
 * This file only adapts Vite's build-time environment into the shape `resolvePolicy` wants,
 * so the browser pilot and the desktop agent are configured by the same rules and validated
 * by the same code.
 *
 * Two sources, in increasing precedence:
 *
 * 1. **Build-time**, via Vite. Only `VITE_`-prefixed variables are exposed, so an override is
 *    written `VITE_FOCUS_AGENT__THRESHOLDS__YAW_FORWARD_MAX=25` and the prefix is stripped here.
 * 2. **Runtime**, via `window.__RUNTIME_CONFIG__`, which the container entrypoint writes into
 *    `/env-config.js` from any `RUNTIME_*` variable on the pod. Set on the deployment as
 *    `RUNTIME_FOCUS_AGENT__SESSION_GUARD__FACE_ABSENT_LOCK_SECONDS=20`; the entrypoint strips
 *    `RUNTIME_`, so the browser sees `FOCUS_AGENT__SESSION_GUARD__FACE_ABSENT_LOCK_SECONDS`.
 *
 * Runtime wins, because the point of it is to retune a deployed environment without rebuilding
 * an image. Both go through the same `resolvePolicy` validation, so a nonsensical value is
 * rejected identically whichever way it arrived.
 *
 * What deliberately cannot be set either way: `sessionGuard.enabled` and `sessionGuard.mode`.
 * Those stay in the host's own overrides. Runtime config reaches the browser as a script the
 * page fetches, so anything read from it is ultimately under the control of the person being
 * monitored — and an on/off switch for a monitoring control does not belong there. Lock
 * *durations* are safe to expose: there is nothing to gain by lengthening your own dwell.
 */

import { resolvePolicy, type ResolvedPolicy } from '../core/index.js';

const VITE_PREFIX = 'VITE_';

/**
 * Read build-time environment.
 *
 * `import.meta.env` is referenced **literally** on purpose. Vite substitutes that exact
 * expression at transform time, so assigning `import.meta` to a variable first and reading
 * `.env` off it defeats the substitution and yields `undefined` — which silently disabled every
 * override this function exists to read, and made the asset base fall back to `/`.
 */
function ambientEnv(): Record<string, unknown> {
  return (import.meta.env ?? {}) as unknown as Record<string, unknown>;
}

/**
 * Runtime keys arrive **without** a prefix.
 *
 * The container entrypoint reads `RUNTIME_FOCUS_AGENT__...` from the pod environment and strips
 * `RUNTIME_` before writing the key into `window.__RUNTIME_CONFIG__`, so what reaches the browser
 * is a bare `FOCUS_AGENT__...`. Matching on `RUNTIME_` here found nothing and made every runtime
 * override silently inert — verified by running the real entrypoint rather than reading it.
 */
const RUNTIME_PREFIX = '';

/**
 * Read the runtime config the container entrypoint injected, if any.
 *
 * Absent in local development and in tests, where the build-time path is the only one. Guarded
 * because `/env-config.js` may legitimately not have loaded — a missing file must leave the
 * shipped defaults in force rather than throw during camera start-up.
 */
function runtimeEnv(): Record<string, unknown> {
  if (typeof window === 'undefined') return {};
  const config = (window as { __RUNTIME_CONFIG__?: unknown }).__RUNTIME_CONFIG__;
  return config !== null && typeof config === 'object'
    ? (config as Record<string, unknown>)
    : {};
}

/**
 * Collect `FOCUS_AGENT__*` overrides from both sources, runtime last so it wins.
 *
 * Each source carries its own prefix (`VITE_` or `RUNTIME_`), which is stripped so both arrive
 * at `resolvePolicy` in the single form it understands.
 */
export function policyEnv(
  source: Record<string, unknown> = ambientEnv(),
  runtime: Record<string, unknown> = runtimeEnv(),
): Record<string, string> {
  const env: Record<string, string> = {};
  const collect = (from: Record<string, unknown>, prefix: string): void => {
    for (const [key, value] of Object.entries(from)) {
      if (typeof value !== 'string') continue;
      if (!key.startsWith(`${prefix}FOCUS_AGENT`)) continue;
      env[key.slice(prefix.length)] = value;
    }
  };
  collect(source, VITE_PREFIX);
  collect(runtime, RUNTIME_PREFIX);
  return env;
}

/**
 * Resolve the policy once per page load.
 *
 * A validation failure is surfaced rather than swallowed: starting a camera under a policy
 * the code knows is incoherent is worse than showing the operator what is wrong.
 */
let cached: ResolvedPolicy | null = null;

/**
 * Prefix a root-relative asset path with the app's base URL.
 *
 * The admin app is served under `base: '/admin/'`, so an absolute `/models/...` resolves to the
 * server root and 404s. The policy defaults are written root-relative because they must also
 * work for a host mounted at `/`, so the base is applied here rather than baked into them.
 * Left untouched if the value is already absolute or already carries the base.
 */
function withBase(path: string, base: string): string {
  if (!path.startsWith('/')) return path;
  const normalised = base.endsWith('/') ? base.slice(0, -1) : base;
  if (normalised === '' || path.startsWith(`${normalised}/`)) return path;
  return `${normalised}${path}`;
}

export function browserPolicy(): ResolvedPolicy {
  if (cached === null) {
    // Literal `import.meta.env.BASE_URL` — see ambientEnv() on why it must not be aliased.
    const rawBase: unknown = import.meta.env.BASE_URL;
    const base = typeof rawBase === 'string' && rawBase.length > 0 ? rawBase : '/';
    const resolved = resolvePolicy({ env: policyEnv() });
    cached = {
      ...resolved,
      policy: {
        ...resolved.policy,
        vision: {
          ...resolved.policy.vision,
          modelAssetPath: withBase(resolved.policy.vision.modelAssetPath, base),
          wasmBasePath: withBase(resolved.policy.vision.wasmBasePath, base),
        },
      },
    };
  }
  return cached;
}
